import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TradableUniverse from './TradableUniverse';
import MarketWatch from './MarketWatch';
import Positions from './Positions';
import Orders from './Orders';
import TradingJournal from './TradingJournal';
import LoginScreen from './LoginScreen';
import AdminPanel from './AdminPanel';
import Discrepancies from './Discrepancies';
import Health from './Health';
import './App.css';

const API_BASE = '/api';

// Session header name — must match backend auth-middleware.js
const SESSION_HEADER = 'x-session-id';

function App() {
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem('app_page') || 'tradable-universe';
    return saved === 'discrepancies' ? 'health' : saved; // Discrepancies folded into Health (Session 50)
  });

  // ─── User Auth State ────────────────────────────────────
  const [user, setUser] = useState(null);         // { id, username, displayName, role, canLoginBroker, canLogoutBroker }
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('session_id') || null);
  const [authChecked, setAuthChecked] = useState(false); // Have we checked the session yet?

  // ─── Broker Auth State ──────────────────────────────────
  const [accounts, setAccounts] = useState(null);     // { kite: [...], kotak: [...] }
  const [authenticated, setAuthenticated] = useState(false); // At least one Kite logged in
  const [backendOnline, setBackendOnline] = useState(true);
  const [kotakTotp, setKotakTotp] = useState('');
  const [loginLoading, setLoginLoading] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState('');
  const [demoMode, setDemoMode] = useState(false);
  const [showKotakLogin, setShowKotakLogin] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showBrokerLogout, setShowBrokerLogout] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState('');
  const [disconnectConfirm, setDisconnectConfirm] = useState(null); // null or { accountId, label } or { accountId: 'all' }

  // ─── Set session header on axios globally ───────────────
  useEffect(() => {
    if (sessionId) {
      axios.defaults.headers.common[SESSION_HEADER] = sessionId;
      localStorage.setItem('session_id', sessionId);
    } else {
      delete axios.defaults.headers.common[SESSION_HEADER];
      localStorage.removeItem('session_id');
    }
  }, [sessionId]);

  // ─── On mount — check if we have a valid session ───────
  useEffect(() => {
    if (sessionId) {
      checkSession();
    } else {
      setAuthChecked(true);
    }
  }, []);

  async function checkSession() {
    try {
      // Set header before making the call
      axios.defaults.headers.common[SESSION_HEADER] = sessionId;
      const res = await axios.get(`${API_BASE}/auth/session`);
      if (res.data.authenticated) {
        setUser(res.data.user);
        setBackendOnline(true);
        fetchAccounts();
      } else {
        // Session expired
        setSessionId(null);
        setUser(null);
      }
    } catch (err) {
      // Backend might be down
      setBackendOnline(false);
      setSessionId(null);
      setUser(null);
    }
    setAuthChecked(true);
  }

  // ─── Called when LoginScreen completes ──────────────────
  function handleLogin(newSessionId, userData) {
    setSessionId(newSessionId);
    setUser(userData);
    axios.defaults.headers.common[SESSION_HEADER] = newSessionId;
    localStorage.setItem('session_id', newSessionId);
    setPage('tradable-universe'); // Always land on TU after login — MW/Positions/Orders load SSE in background
    fetchAccounts();
  }

  // ─── Fetch broker accounts ─────────────────────────────
  async function fetchAccounts() {
    try {
      const res = await axios.get(`${API_BASE}/accounts`);
      setAccounts(res.data);
      setBackendOnline(true);
      const anyKiteAuth = res.data.kite?.some(a => a.authenticated) || false;
      setAuthenticated(anyKiteAuth);
    } catch (err) {
      if (err.response?.status === 401) {
        // Session expired
        setSessionId(null);
        setUser(null);
      } else {
        setBackendOnline(false);
      }
    }
  }

  // Persist current page
  useEffect(() => { localStorage.setItem('app_page', page); }, [page]);

  // ─── Kite Login ─────────────────────────────────────────
  async function loginKite(accountId) {
    setLoginLoading(accountId);
    setLoginError('');
    try {
      const res = await axios.get(`${API_BASE}/auth/kite/${accountId}/login-url`);
      window.location.href = res.data.url;
    } catch (err) {
      if (err.response?.status === 401) { setSessionId(null); setUser(null); return; }
      setLoginError(`Failed to get login URL: ${err.response?.data?.error || err.message}`);
      setLoginLoading('');
    }
  }

  // ─── Kotak Login ────────────────────────────────────────
  async function loginKotak(accountId) {
    if (!kotakTotp || kotakTotp.length !== 6) {
      setLoginError('Enter a 6-digit TOTP code');
      return;
    }
    setLoginLoading(accountId);
    setLoginError('');
    try {
      await axios.post(`${API_BASE}/auth/kotak/${accountId}/login`, { totp: kotakTotp });
      setLoginSuccess(`${accountId} logged in!`);
      setKotakTotp('');
      setLoginLoading('');
      setShowKotakLogin(false);
      await fetchAccounts();
    } catch (err) {
      if (err.response?.status === 401) { setSessionId(null); setUser(null); return; }
      const detail = err.response?.data?.error || err.message;
      setLoginError(`Kotak login failed: ${detail}`);
      setLoginLoading('');
    }
  }

  // ─── App Logout (user session only, NOT broker) ────────
  async function handleAppLogout() {
    try {
      await axios.post(`${API_BASE}/auth/app-logout`);
    } catch (err) { /* ignore */ }
    setSessionId(null);
    setUser(null);
    setAuthenticated(false);
    setDemoMode(false);
    setAccounts(null);
    setShowKotakLogin(false);
    setLoginError('');
    setLoginSuccess('');
  }

  // ─── Broker Logout (disconnects all brokers — admin/permission only) ──
  async function handleBrokerLogout() {
    setDisconnectLoading('all');
    try {
      await axios.post(`${API_BASE}/auth/broker-logout`);
    } catch (err) {
      if (err.response?.status === 401) { setSessionId(null); setUser(null); return; }
    }
    setAuthenticated(false);
    setAccounts(null);
    setShowKotakLogin(false);
    setShowBrokerLogout(false);
    setDisconnectLoading('');
    setLoginError('');
    setLoginSuccess('');
    fetchAccounts();
  }

  // ─── Disconnect a single broker account ─────────────────
  async function handleDisconnectAccount(accountId) {
    setDisconnectLoading(accountId);
    try {
      await axios.post(`${API_BASE}/auth/broker-logout/${accountId}`);
    } catch (err) {
      if (err.response?.status === 401) { setSessionId(null); setUser(null); return; }
    }
    setDisconnectLoading('');
    await fetchAccounts();
    // If no brokers left connected, close the popup
    const res = await axios.get(`${API_BASE}/accounts`).catch(() => null);
    if (res) {
      const anyConnected = [...(res.data.kite || []), ...(res.data.kotak || [])].some(a => a.authenticated);
      if (!anyConnected) setShowBrokerLogout(false);
    }
  }

  // ─── Enter demo mode ───────────────────────────────────
  function enterDemoMode() {
    setDemoMode(true);
    setPage('market-watch');
  }

  // ─── Theme ──────────────────────────────────────────────
  const t = {
    bg: '#0f1117', bgCard: '#181b27', border: '#1e2130', headerBg: '#13151e',
    text: '#e1e4ea', textMuted: '#8b8fa3', textDim: '#4a5568',
    green: '#22c55e', red: '#ef4444', blue: '#3b82f6', yellow: '#fbbf24',
    bgInput: '#1a1d2a', borderInput: '#2a2d3a',
  };

  // ═══════════════════════════════════════════════════════════
  // LOADING — checking session
  // ═══════════════════════════════════════════════════════════
  if (!authChecked) {
    return (
      <div style={{ fontFamily: "'Segoe UI', sans-serif", background: t.bg, color: t.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: t.textMuted, fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // NOT LOGGED IN — show LoginScreen
  // ═══════════════════════════════════════════════════════════
  if (!user && !demoMode) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // ═══════════════════════════════════════════════════════════
  // MAIN APP (user logged in or demo mode)
  // ═══════════════════════════════════════════════════════════

  const kiteAccounts = accounts?.kite || [];
  const kotakAccounts = accounts?.kotak || [];

  return (
    <div className="App" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Navigation bar */}
      <nav style={{
        display: 'flex', alignItems: 'center', gap: 0,
        background: '#111827', borderBottom: '1px solid #1f2937',
        padding: '0 16px', height: 40, flexShrink: 0, position: 'relative'
      }}>
        <button onClick={() => setPage('tradable-universe')} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: page === 'tradable-universe' ? 600 : 400,
          background: page === 'tradable-universe' ? '#1f2937' : 'transparent',
          color: page === 'tradable-universe' ? '#f9fafb' : '#9ca3af',
          border: 'none', cursor: 'pointer', borderBottom: page === 'tradable-universe' ? '2px solid #3b82f6' : '2px solid transparent'
        }}>Tradable Universe</button>

        <button onClick={() => setPage('market-watch')} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: page === 'market-watch' ? 600 : 400,
          background: page === 'market-watch' ? '#1f2937' : 'transparent',
          color: page === 'market-watch' ? '#f9fafb' : '#9ca3af',
          border: 'none', cursor: 'pointer', borderBottom: page === 'market-watch' ? '2px solid #3b82f6' : '2px solid transparent'
        }}>Market Watch</button>

        <button onClick={() => setPage('positions')} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: page === 'positions' ? 600 : 400,
          background: page === 'positions' ? '#1f2937' : 'transparent',
          color: page === 'positions' ? '#f9fafb' : '#9ca3af',
          border: 'none', cursor: 'pointer', borderBottom: page === 'positions' ? '2px solid #3b82f6' : '2px solid transparent'
        }}>Positions</button>

        <button onClick={() => setPage('orders')} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: page === 'orders' ? 600 : 400,
          background: page === 'orders' ? '#1f2937' : 'transparent',
          color: page === 'orders' ? '#f9fafb' : '#9ca3af',
          border: 'none', cursor: 'pointer', borderBottom: page === 'orders' ? '2px solid #3b82f6' : '2px solid transparent'
        }}>Orders</button>

        <button onClick={() => setPage('journal')} style={{
          padding: '8px 16px', fontSize: 13, fontWeight: page === 'journal' ? 600 : 400,
          background: page === 'journal' ? '#1f2937' : 'transparent',
          color: page === 'journal' ? '#f9fafb' : '#9ca3af',
          border: 'none', cursor: 'pointer', borderBottom: page === 'journal' ? '2px solid #3b82f6' : '2px solid transparent'
        }}>Journal</button>

        {/* Admin tab — only visible to admin users */}
        {user?.role === 'admin' && (
          <button onClick={() => setPage('admin')} style={{
            padding: '8px 16px', fontSize: 13, fontWeight: page === 'admin' ? 600 : 400,
            background: page === 'admin' ? '#1f2937' : 'transparent',
            color: page === 'admin' ? '#f9fafb' : '#9ca3af',
            border: 'none', cursor: 'pointer', borderBottom: page === 'admin' ? '2px solid #3b82f6' : '2px solid transparent'
          }}>Admin</button>
        )}

        {/* Discrepancies tab removed (Session 50) — folded into the Health tab. */}

        {/* Health tab — admin only. Unified binary health view + folded-in discrepancies. */}
        {user?.role === 'admin' && (
          <button onClick={() => setPage('health')} style={{
            padding: '8px 16px', fontSize: 13, fontWeight: page === 'health' ? 600 : 400,
            background: page === 'health' ? '#1f2937' : 'transparent',
            color: page === 'health' ? '#f9fafb' : '#9ca3af',
            border: 'none', cursor: 'pointer', borderBottom: page === 'health' ? '2px solid #3b82f6' : '2px solid transparent'
          }}>Health</button>
        )}

        {/* Right side */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, fontSize: 11 }}>
          {/* Broker status dots */}
          {kiteAccounts.map(acct => (
            <span key={acct.id} style={{ display: 'flex', alignItems: 'center', gap: 4, color: acct.authenticated ? '#22c55e' : '#6b7280' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: acct.authenticated ? '#22c55e' : '#4b5563', display: 'inline-block' }} />
              {acct.label || acct.id}
            </span>
          ))}
          {kotakAccounts.map(acct => (
            <span key={acct.id} style={{ display: 'flex', alignItems: 'center', gap: 4, color: acct.authenticated ? '#22c55e' : '#6b7280' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: acct.authenticated ? '#22c55e' : '#4b5563', display: 'inline-block' }} />
              {acct.label || acct.id}
            </span>
          ))}

          {/* Login button for broker accounts (only if user has permission) */}
          {!demoMode && user?.canLoginBroker && [...kiteAccounts, ...kotakAccounts].some(a => !a.authenticated) && (
            <button
              onClick={() => { setShowKotakLogin(!showKotakLogin); setLoginError(''); setLoginSuccess(''); }}
              style={{ padding: '4px 14px', fontSize: 11, fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Broker Login
            </button>
          )}

          {/* Broker Logout (only if user has permission + at least one broker is connected) */}
          {!demoMode && user?.canLogoutBroker && authenticated && (
            <button
              onClick={() => { setShowBrokerLogout(!showBrokerLogout); setShowKotakLogin(false); }}
              style={{ padding: '4px 14px', fontSize: 11, fontWeight: 500, background: 'transparent', color: '#ef4444', border: '1px solid #4a2020', borderRadius: 4, cursor: 'pointer' }}
            >
              Broker Logout
            </button>
          )}

          {/* Demo badge */}
          {demoMode && (
            <span style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, background: '#92400e', color: '#fbbf24', borderRadius: 3 }}>DEMO</span>
          )}

          {/* User info + App Logout */}
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: '1px solid #2a2d3a', paddingLeft: 12 }}>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {user.displayName}
                <span style={{ marginLeft: 4, padding: '1px 5px', fontSize: 9, fontWeight: 600, borderRadius: 3,
                  background: user.role === 'admin' ? '#1e3a5f' : user.role === 'trader' ? '#1a3a1a' : '#3a3a1a',
                  color: user.role === 'admin' ? '#60a5fa' : user.role === 'trader' ? '#4ade80' : '#fbbf24'
                }}>
                  {user.role.toUpperCase()}
                </span>
              </span>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                style={{ padding: '3px 10px', fontSize: 10, fontWeight: 500, background: 'transparent', color: '#8b8fa3', border: '1px solid #2a2d3a', borderRadius: 3, cursor: 'pointer' }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Broker login popup */}
      {showKotakLogin && [...kiteAccounts, ...kotakAccounts].some(a => !a.authenticated) && (
        <div style={{
          position: 'absolute', top: 40, right: 16, zIndex: 50,
          background: '#181b27', border: '1px solid #2a2d3a', borderRadius: 8,
          padding: 16, width: 300, boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#e1e4ea', marginBottom: 12 }}>Login to Broker Account</div>
          {loginError && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{loginError}</div>}
          {loginSuccess && <div style={{ fontSize: 11, color: '#22c55e', marginBottom: 8 }}>{loginSuccess}</div>}

          {/* Unauthenticated Kite accounts */}
          {kiteAccounts.filter(a => !a.authenticated).map(acct => (
            <div key={acct.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #2a2d3a' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#e1e4ea' }}>{acct.label || acct.id}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>Kite Connect — {acct.id === 'KITE_1' ? 'primary data + trading' : 'trading'}</div>
              </div>
              <button
                onClick={() => loginKite(acct.id)}
                disabled={loginLoading === acct.id}
                style={{ padding: '5px 14px', fontSize: 11, fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 5, cursor: loginLoading === acct.id ? 'wait' : 'pointer', opacity: loginLoading === acct.id ? 0.6 : 1 }}
              >
                {loginLoading === acct.id ? 'Redirecting...' : 'Login'}
              </button>
            </div>
          ))}

          {/* Unauthenticated Kotak accounts */}
          {kotakAccounts.filter(a => !a.authenticated).map(acct => (
            <div key={acct.id} style={{ padding: '8px 0', borderBottom: '1px solid #2a2d3a' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#e1e4ea' }}>{acct.label || acct.id}</div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Kotak Neo</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text" placeholder="6-digit TOTP" value={kotakTotp}
                  onChange={e => setKotakTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6} autoFocus
                  style={{ flex: 1, padding: '7px 10px', fontSize: 12, background: '#1a1d2a', border: '1px solid #2a2d3a', borderRadius: 5, color: '#e1e4ea', outline: 'none', letterSpacing: 4, textAlign: 'center', fontWeight: 600 }}
                  onKeyDown={e => e.key === 'Enter' && loginKotak(acct.id)}
                />
                <button
                  onClick={() => loginKotak(acct.id)}
                  disabled={loginLoading === acct.id || kotakTotp.length !== 6}
                  style={{ padding: '7px 14px', fontSize: 11, fontWeight: 600, background: '#f97316', color: '#fff', border: 'none', borderRadius: 5,
                    cursor: (loginLoading === acct.id || kotakTotp.length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (loginLoading === acct.id || kotakTotp.length !== 6) ? 0.5 : 1
                  }}
                >
                  {loginLoading === acct.id ? '...' : 'Go'}
                </button>
              </div>
            </div>
          ))}

          <button onClick={() => setShowKotakLogin(false)} style={{ marginTop: 10, fontSize: 10, color: '#8b8fa3', background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
        </div>
      )}

      {/* Broker Logout popup — choose which account to disconnect */}
      {showBrokerLogout && (
        <div style={{
          position: 'absolute', top: 40, right: 16, zIndex: 50,
          background: '#181b27', border: '1px solid #2a2d3a', borderRadius: 8,
          padding: 16, width: 300, boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#e1e4ea', marginBottom: 12 }}>Disconnect Broker Account</div>

          {/* Connected accounts with individual Disconnect buttons */}
          {[...kiteAccounts, ...kotakAccounts].filter(a => a.authenticated).map(acct => (
            <div key={acct.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #2a2d3a' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#e1e4ea' }}>{acct.label || acct.id}</div>
                <div style={{ fontSize: 10, color: '#22c55e' }}>● Connected</div>
              </div>
              <button
                onClick={() => setDisconnectConfirm({ accountId: acct.id, label: acct.label || acct.id })}
                disabled={!!disconnectLoading}
                style={{ padding: '5px 14px', fontSize: 11, fontWeight: 500, background: 'transparent', color: '#ef4444', border: '1px solid #4a2020', borderRadius: 5, cursor: disconnectLoading ? 'wait' : 'pointer', opacity: disconnectLoading ? 0.6 : 1 }}
              >
                {disconnectLoading === acct.id ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          ))}

          {/* Disconnect All button */}
          <button
            onClick={() => setDisconnectConfirm({ accountId: 'all', label: 'all broker accounts' })}
            disabled={!!disconnectLoading}
            style={{ marginTop: 12, width: '100%', padding: '8px', fontSize: 11, fontWeight: 600, background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: 5, cursor: disconnectLoading ? 'wait' : 'pointer', opacity: disconnectLoading ? 0.6 : 1 }}
          >
            {disconnectLoading === 'all' ? 'Disconnecting all...' : 'Disconnect All'}
          </button>

          <button onClick={() => setShowBrokerLogout(false)} style={{ marginTop: 10, fontSize: 10, color: '#8b8fa3', background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
        </div>
      )}

      {/* Broker disconnect confirmation popup */}
      {disconnectConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setDisconnectConfirm(null)}>
          <div style={{
            background: '#181b27', border: '1px solid #2a2d3a', borderRadius: 10,
            padding: 24, width: 360, textAlign: 'center'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#e1e4ea', marginBottom: 8 }}>Disconnect {disconnectConfirm.label}?</div>
            <div style={{ fontSize: 12, color: '#8b8fa3', marginBottom: 20, lineHeight: 1.5 }}>
              {disconnectConfirm.accountId === 'all'
                ? 'Are you sure you want to disconnect ALL broker accounts? This will stop live data for everyone using the app.'
                : `Are you sure you want to disconnect ${disconnectConfirm.label}? Live data from this account will stop for all users.`
              }
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setDisconnectConfirm(null)}
                style={{ padding: '8px 24px', fontSize: 12, background: 'transparent', color: '#8b8fa3', border: '1px solid #2a2d3a', borderRadius: 6, cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={() => {
                  const id = disconnectConfirm.accountId;
                  setDisconnectConfirm(null);
                  if (id === 'all') handleBrokerLogout();
                  else handleDisconnectAccount(id);
                }}
                style={{ padding: '8px 24px', fontSize: 12, fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >Yes, Disconnect</button>
            </div>
          </div>
        </div>
      )}

      {/* Logout confirmation popup */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowLogoutConfirm(false)}>
          <div style={{
            background: '#181b27', border: '1px solid #2a2d3a', borderRadius: 10,
            padding: 24, width: 320, textAlign: 'center'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#e1e4ea', marginBottom: 8 }}>Logout?</div>
            <div style={{ fontSize: 12, color: '#8b8fa3', marginBottom: 20 }}>Are you sure you want to log out of the app?</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{ padding: '8px 24px', fontSize: 12, background: 'transparent', color: '#8b8fa3', border: '1px solid #2a2d3a', borderRadius: 6, cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={() => { setShowLogoutConfirm(false); handleAppLogout(); }}
                style={{ padding: '8px 24px', fontSize: 12, fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >Yes, Logout</button>
            </div>
          </div>
        </div>
      )}

      {/* Page content — all pages always mounted, inactive pages hidden with display:none.
          SSE connections stay alive on all pages, tab switching is instant. */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: '#0f1117' }}>
        <div style={{ display: page === 'tradable-universe' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
          <TradableUniverse demoMode={demoMode} />
        </div>
        <div style={{ display: page === 'market-watch' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <MarketWatch demoMode={demoMode} sessionId={sessionId} user={user} />
        </div>
        <div style={{ display: page === 'positions' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Positions demoMode={demoMode} sessionId={sessionId} brokerAuthenticated={authenticated} user={user} />
        </div>
        <div style={{ display: page === 'orders' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Orders demoMode={demoMode} sessionId={sessionId} brokerAuthenticated={authenticated} user={user} />
        </div>
        <div style={{ display: page === 'journal' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
          <TradingJournal />
        </div>
        {user?.role === 'admin' && (
          <div style={{ display: page === 'admin' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
            <AdminPanel />
          </div>
        )}
        {user?.role === 'admin' && (
          <div style={{ display: page === 'discrepancies' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
            <Discrepancies sessionId={sessionId} />
          </div>
        )}
        {user?.role === 'admin' && (
          <div style={{ display: page === 'health' ? 'flex' : 'none', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
            <Health sessionId={sessionId} />
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
